import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { trackEvent } from 'user-analytics';
import { search as searchApi } from 'js-api-client';
import { buildSearchUrl, buildReturnUrl, buildProviderListUrl } from 'utils/urls';
import { getMomentFromSearchDateParam } from 'utils/search/searchDateParam';
import { getCalendarDayFinalPrice, getCalendarDayPrice } from 'utils/search/calendarDayPrice';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import useFinalPriceExperiment from 'hooks/useFinalPriceExperiment';
import { setCarouselSession } from 'actions/carouselSession';
import {
  DAYS_CAROUSEL_VIEWED,
  DAYS_CAROUSEL_DAY_SELECTED,
  DAYS_CAROUSEL_SCROLLED,
  DAYS_CAROUSEL_PRICES_LOADED,
} from 'constants/TrackEvents';
import DaysCarousel from './DaysCarousel';
import useShowDaysCarousel from './useShowDaysCarousel';

const DEFAULT_MAX_DAYS = 30;
const CALENDAR_WINDOW_DAYS = 14;
/**
 * Price cache keyed by search context and date to prevent mixing prices across
 * different routes, trip types, and trip legs.
 */
const calendarPriceCache = {};
// The markup-inclusive price of each cached day (BOOK-176), filled from the same responses so the
// arm is picked at render and never re-requests a range.
const calendarFinalPriceCache = {};
const calendarLoadingCache = {};
const requestedCalendarDates = {};
const calendarStateListeners = new Set();
const activeCalendarRequests = {};

/**
 * Clears module-level calendar cache. Intended for Jest isolation.
 */
export const resetCalendarCacheForTests = () => {
  Object.keys(calendarPriceCache).forEach((key) => delete calendarPriceCache[key]);
  Object.keys(calendarFinalPriceCache).forEach((key) => delete calendarFinalPriceCache[key]);
  Object.keys(calendarLoadingCache).forEach((key) => delete calendarLoadingCache[key]);
  Object.keys(requestedCalendarDates).forEach((key) => delete requestedCalendarDates[key]);
  Object.keys(activeCalendarRequests).forEach((key) => delete activeCalendarRequests[key]);
  calendarStateListeners.clear();
};

/**
 * Notifies mounted carousel instances that module-level calendar state changed.
 */
const notifyCalendarStateListeners = () => {
  calendarStateListeners.forEach((listener) => listener());
};

/**
 * Tracks an active calendar request for a route context.
 * @param {string} routeKey - Route context key.
 * @param {Promise & { abort?: Function }} request - Calendar request promise.
 */
const trackCalendarRequest = (routeKey, request) => {
  if (!activeCalendarRequests[routeKey]) {
    activeCalendarRequests[routeKey] = new Set();
  }

  activeCalendarRequests[routeKey].add(request);
};

/**
 * Removes a finished calendar request from its route context.
 * @param {string} routeKey - Route context key.
 * @param {Promise & { abort?: Function }} request - Calendar request promise.
 */
const untrackCalendarRequest = (routeKey, request) => {
  activeCalendarRequests[routeKey]?.delete(request);

  if (activeCalendarRequests[routeKey]?.size === 0) {
    delete activeCalendarRequests[routeKey];
  }
};

/**
 * Aborts active calendar requests that belong to a different route.
 * @param {string} routeKey - Route context key to keep active.
 */
const abortCalendarRequestsForOtherRoutes = (routeKey) => {
  Object.entries(activeCalendarRequests).forEach(([activeRouteKey, requests]) => {
    if (activeRouteKey === routeKey) return;

    requests.forEach((request) => request.abort?.());
    delete activeCalendarRequests[activeRouteKey];
  });
};

/**
 * Aborts active calendar requests for a route.
 * @param {string} routeKey - Route context key to abort.
 */
const abortCalendarRequestsForRoute = (routeKey) => {
  activeCalendarRequests[routeKey]?.forEach((request) => request.abort?.());
  delete activeCalendarRequests[routeKey];
};

/**
 * Build the days array for the DayPriceSelector.
 * @param {string} startISO - Start date in YYYY-MM-DD format.
 * @param {string} endISO - End date in YYYY-MM-DD format.
 * @returns {Array} Array of day objects.
 */
const buildDays = (startISO, endISO) => {
  const days = [];
  const current = getMomentFromSearchDateParam(startISO);
  const end = getMomentFromSearchDateParam(endISO);

  if (!current.isValid() || !end.isValid()) return days;

  while (current.isSameOrBefore(end, 'day')) {
    days.push({ dateISO: current.format('YYYY-MM-DD'), departures: 1, badge: null });
    current.add(1, 'day');
  }

  return days;
};

/**
 * Clamps a calendar window to the carousel date boundaries.
 * @param {object} params - Window params.
 * @param {moment.Moment} params.fromMoment - Requested start date.
 * @param {moment.Moment} params.toMoment - Requested end date.
 * @param {string} params.startISO - Carousel start date.
 * @param {string} params.endISO - Carousel end date.
 * @returns {{ from: string, to: string }|null} Clamped range.
 */
const clampCalendarWindow = ({ fromMoment, toMoment, startISO, endISO }) => {
  const startMoment = moment(startISO, 'YYYY-MM-DD');
  const endMoment = moment(endISO, 'YYYY-MM-DD');
  const from = moment.max(fromMoment, startMoment);
  const to = moment.min(toMoment, endMoment);

  if (!from.isValid() || !to.isValid() || from.isAfter(to, 'day')) return null;

  return {
    from: from.format('YYYY-MM-DD'),
    to: to.format('YYYY-MM-DD'),
  };
};

/**
 * Mark an inclusive range as requested for a context.
 * @param {string} contextKey - Calendar request context key.
 * @param {string} fromISO - Start date in YYYY-MM-DD format.
 * @param {string} toISO - End date in YYYY-MM-DD format.
 */
const markRequestedCalendarRange = (contextKey, fromISO, toISO) => {
  if (!requestedCalendarDates[contextKey]) {
    requestedCalendarDates[contextKey] = new Set();
  }

  const current = moment(fromISO, 'YYYY-MM-DD');
  const end = moment(toISO, 'YYYY-MM-DD');

  while (current.isSameOrBefore(end, 'day')) {
    requestedCalendarDates[contextKey].add(current.format('YYYY-MM-DD'));
    current.add(1, 'day');
  }
};

/**
 * Clears an inclusive range from the requested dates for a context.
 * @param {string} contextKey - Calendar request context key.
 * @param {string} fromISO - Start date in YYYY-MM-DD format.
 * @param {string} toISO - End date in YYYY-MM-DD format.
 */
const clearRequestedCalendarRange = (contextKey, fromISO, toISO) => {
  const requestedDates = requestedCalendarDates[contextKey];
  if (!requestedDates) return;

  const current = moment(fromISO, 'YYYY-MM-DD');
  const end = moment(toISO, 'YYYY-MM-DD');

  while (current.isSameOrBefore(end, 'day')) {
    requestedDates.delete(current.format('YYYY-MM-DD'));
    current.add(1, 'day');
  }

  if (requestedDates.size === 0) {
    delete requestedCalendarDates[contextKey];
  }
};

/**
 * Checks whether an error came from aborting a calendar request.
 * @param {Error} error - Calendar request error.
 * @returns {boolean} Whether the error is an abort error.
 */
const isAbortCalendarRequestError = (error) =>
  error?.name === 'AbortError' || error?.code === 'ABORT_ERR';

/**
 * Build a date-keyed state patch for an inclusive range.
 * @param {string} contextKey - Calendar request context key.
 * @param {string} fromISO - Start date in YYYY-MM-DD format.
 * @param {string} toISO - End date in YYYY-MM-DD format.
 * @param {boolean} value - Value to assign to each date key.
 * @returns {object} State patch keyed by date cache key.
 */
const buildDateRangeState = (contextKey, fromISO, toISO, value) => {
  const rangeState = {};
  const current = moment(fromISO, 'YYYY-MM-DD');
  const end = moment(toISO, 'YYYY-MM-DD');

  while (current.isSameOrBefore(end, 'day')) {
    rangeState[`${contextKey}|${current.format('YYYY-MM-DD')}`] = value;
    current.add(1, 'day');
  }

  return rangeState;
};

/**
 * Counts calendar days with prices and availability for tracking.
 * @param {object} response - Calendar endpoint response.
 * @param {Array<string>} priceWays - Calendar legs to inspect.
 * @returns {{ pricedDaysCount: number, availableDaysCount: number }} Calendar metrics.
 */
const getCalendarResponseMetrics = (response, priceWays) =>
  priceWays.reduce(
    (metrics, priceWay) => {
      const calendarDays = response?.[priceWay] ?? [];

      return calendarDays.reduce(
        (wayMetrics, calendarDay) => ({
          pricedDaysCount: wayMetrics.pricedDaysCount + (calendarDay.display_price ? 1 : 0),
          availableDaysCount: wayMetrics.availableDaysCount + (calendarDay.available ? 1 : 0),
        }),
        metrics,
      );
    },
    { pricedDaysCount: 0, availableDaysCount: 0 },
  );

/**
 * DaysCarouselContainer component.
 * @param {object} props - way.
 * @param {string} props.way - Trip segment: 'departure' or 'return'.
 * @param {string} props.containerClassName - Class name for the container div.
 * @param {boolean} props.isProviderList - Whether navigation should stay on provider selection.
 */
const DaysCarouselContainer = ({ way, containerClassName, isProviderList }) => {
  const showDaysCarousel = useShowDaysCarousel();
  const features = useWhitelabelFeatures();
  const calendarPricesEnabled = features?.CALENDAR_AVAILABILITY_ENABLED === true;
  const scrollCountRef = useRef(0);
  const [calendarPrices, setCalendarPrices] = useState({ ...calendarPriceCache });
  const [calendarFinalPrices, setCalendarFinalPrices] = useState({ ...calendarFinalPriceCache });
  const [loadingCalendarPrices, setLoadingCalendarPrices] = useState({ ...calendarLoadingCache });
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {
    originSlug,
    destinationSlug,
    departureDate,
    returnDate,
    passengers,
    departureId,
    providerId,
  } = useParams();

  const { ticket: { nit: exchangeNit } = {}, operationNumbers: exchangeOperationNumbers } =
    useSelector((state) => state.exchange) || {};
  const { maxDays, storedOriginalSearchDate } = useSelector(
    ({ whitelabelConfig, carouselSession }) => {
      const maxDays = whitelabelConfig.env?.searchWidget?.maxDaysSearch || DEFAULT_MAX_DAYS;

      return {
        maxDays,
        storedOriginalSearchDate: carouselSession[way]?.originalSearchDate ?? null,
      };
    },
  );

  const isOpen = window.location.pathname.split('/').pop() === 'open';
  const isRoundTrip = !!returnDate;

  const selectedMoment = getMomentFromSearchDateParam(
    way === 'departure' ? departureDate : returnDate,
  );
  const selectedDateISO = selectedMoment.isValid() ? selectedMoment.format('YYYY-MM-DD') : null;
  const todayMoment = moment().startOf('day');

  const startISO = way === 'departure' ? todayMoment.format('YYYY-MM-DD') : departureDate;

  const maxSearchDate = todayMoment.clone().add(maxDays, 'days');
  const endISO = maxSearchDate.format('YYYY-MM-DD');
  const returnDateMoment = getMomentFromSearchDateParam(returnDate);
  const tripContext = isRoundTrip ? 'round' : 'one-way';
  const providerScope = providerId || 'all';
  const calendarRequestContextKey = [originSlug, destinationSlug, tripContext, providerScope].join(
    '|',
  );
  const calendarRouteKey = [originSlug, destinationSlug, providerScope].join('|');
  const buildCalendarPriceContextKey = useCallback(
    (priceWay) => [priceWay, originSlug, destinationSlug, tripContext, providerScope].join('|'),
    [destinationSlug, originSlug, providerScope, tripContext],
  );

  /**
   * Build the cache key for the price cache.
   * Uses `|` as separator to avoid collisions with slugs that contain hyphens.
   * @param {string} dateISO - Date ISO string.
   * @returns {string} Cache key.
   */
  const cacheKey = useCallback(
    (dateISO, priceWay = way) => `${buildCalendarPriceContextKey(priceWay)}|${dateISO}`,
    [buildCalendarPriceContextKey, way],
  );

  const buildLoadingRangeState = useCallback(
    (fromISO, toISO, value) => {
      const priceWays = isRoundTrip ? ['departure', 'return'] : [way];

      return priceWays.reduce(
        (rangeState, priceWay) => ({
          ...rangeState,
          ...buildDateRangeState(buildCalendarPriceContextKey(priceWay), fromISO, toISO, value),
        }),
        {},
      );
    },
    [buildCalendarPriceContextKey, isRoundTrip, way],
  );

  const trackCalendarPricesLoaded = useCallback(
    ({ fromISO, toISO, response, requestStatus, retryCount = 0 }) => {
      const priceWays = isRoundTrip ? ['departure', 'return'] : [way];
      const { pricedDaysCount, availableDaysCount } = getCalendarResponseMetrics(
        response,
        priceWays,
      );

      trackEvent(DAYS_CAROUSEL_PRICES_LOADED, {
        'Way': way,
        'Origin': originSlug,
        'Destination': destinationSlug,
        'Trip Type': isRoundTrip ? 'round' : 'one-way',
        'From': fromISO,
        'To': toISO,
        'Request Status': requestStatus,
        'Retry Count': retryCount,
        'Priced Days Count': pricedDaysCount,
        'Available Days Count': availableDaysCount,
      });
    },
    [destinationSlug, isRoundTrip, originSlug, way],
  );

  const applyCalendarResponse = useCallback(
    (response) => {
      const priceWays = isRoundTrip ? ['departure', 'return'] : [way];
      const nextPrices = {};
      const nextFinalPrices = {};

      priceWays.forEach((priceWay) => {
        const calendarDays = response?.[priceWay] ?? [];

        calendarDays.forEach((calendarDay) => {
          const dateISO = calendarDay.date;
          const price = getCalendarDayPrice({
            calendarDay,
            providerId,
            priceWay,
            isRoundTrip,
          });
          const dateCacheKey = cacheKey(dateISO, priceWay);
          calendarPriceCache[dateCacheKey] = price;
          nextPrices[dateCacheKey] = price;
          const finalPrice = getCalendarDayFinalPrice({
            calendarDay,
            providerId,
            priceWay,
            isRoundTrip,
          });
          calendarFinalPriceCache[dateCacheKey] = finalPrice;
          nextFinalPrices[dateCacheKey] = finalPrice;
        });
      });

      if (Object.keys(nextPrices).length) {
        setCalendarPrices((currentPrices) => ({ ...currentPrices, ...nextPrices }));
        setCalendarFinalPrices((currentPrices) => ({ ...currentPrices, ...nextFinalPrices }));
        notifyCalendarStateListeners();
      }
    },
    [cacheKey, isRoundTrip, providerId, way],
  );

  const requestCalendarRange = useCallback(
    (fromISO, toISO) => {
      if (
        !showDaysCarousel ||
        !calendarPricesEnabled ||
        !originSlug ||
        !destinationSlug ||
        !fromISO ||
        !toISO
      )
        return;

      abortCalendarRequestsForOtherRoutes(calendarRouteKey);
      markRequestedCalendarRange(calendarRequestContextKey, fromISO, toISO);
      const loadingRangeState = buildLoadingRangeState(fromISO, toISO, true);
      const idleRangeState = buildLoadingRangeState(fromISO, toISO, false);
      Object.assign(calendarLoadingCache, loadingRangeState);
      setLoadingCalendarPrices((currentLoading) => ({ ...currentLoading, ...loadingRangeState }));
      notifyCalendarStateListeners();

      const params = {
        origin: originSlug,
        destination: destinationSlug,
        from: fromISO,
        to: toISO,
        round: isRoundTrip,
      };

      /**
       * Runs a calendar request and retries it once when it fails.
       * @param {number} remainingRetries - Number of remaining request retries.
       * @param {number} retryCount - Number of retries already used.
       * @returns {Promise} Calendar request promise.
       */
      const runCalendarRequest = (remainingRetries = 1, retryCount = 0) => {
        const calendarRequest = searchApi.getSearchCalendar(params, {
          onReceiveSearchCalendar: ({ payload }) => {
            applyCalendarResponse(payload);
          },
          options: {
            maxRetries: 10,
          },
        });
        trackCalendarRequest(calendarRouteKey, calendarRequest);
        // Keep activeCalendarRequests limited to in-flight requests so route changes only abort pending work.
        calendarRequest.then(
          () => untrackCalendarRequest(calendarRouteKey, calendarRequest),
          () => untrackCalendarRequest(calendarRouteKey, calendarRequest),
        );

        return calendarRequest
          .then((response) => {
            applyCalendarResponse(response);
            trackCalendarPricesLoaded({
              fromISO,
              toISO,
              response,
              requestStatus: 'success',
              retryCount,
            });
          })
          .catch((error) => {
            if (isAbortCalendarRequestError(error)) {
              clearRequestedCalendarRange(calendarRequestContextKey, fromISO, toISO);
              trackCalendarPricesLoaded({
                fromISO,
                toISO,
                requestStatus: 'aborted',
                retryCount,
              });
              return null;
            }

            if (remainingRetries > 0) {
              return runCalendarRequest(remainingRetries - 1, retryCount + 1);
            }

            clearRequestedCalendarRange(calendarRequestContextKey, fromISO, toISO);
            trackCalendarPricesLoaded({
              fromISO,
              toISO,
              requestStatus: 'failed',
              retryCount,
            });
            return null;
          });
      };

      runCalendarRequest().finally(() => {
        Object.assign(calendarLoadingCache, idleRangeState);
        setLoadingCalendarPrices((currentLoading) => ({ ...currentLoading, ...idleRangeState }));
        notifyCalendarStateListeners();
      });
    },
    [
      applyCalendarResponse,
      buildLoadingRangeState,
      calendarRequestContextKey,
      calendarRouteKey,
      destinationSlug,
      isRoundTrip,
      originSlug,
      showDaysCarousel,
      calendarPricesEnabled,
      trackCalendarPricesLoaded,
    ],
  );

  const requestCalendarWindow = useCallback(
    (direction, anchorDateISO = selectedDateISO) => {
      if (!calendarPricesEnabled) return;
      if (!anchorDateISO) return;
      if (requestedCalendarDates[calendarRequestContextKey]?.has(anchorDateISO)) return;

      let range;

      if (direction === 'previous') {
        const toMoment = moment(anchorDateISO, 'YYYY-MM-DD');
        range = clampCalendarWindow({
          fromMoment: toMoment.clone().subtract(CALENDAR_WINDOW_DAYS - 1, 'days'),
          toMoment,
          startISO,
          endISO,
        });
      } else {
        const fromMoment = moment(anchorDateISO, 'YYYY-MM-DD');
        range = clampCalendarWindow({
          fromMoment,
          toMoment: fromMoment.clone().add(CALENDAR_WINDOW_DAYS - 1, 'days'),
          startISO,
          endISO,
        });
      }

      if (range) requestCalendarRange(range.from, range.to);
    },
    [
      calendarRequestContextKey,
      endISO,
      requestCalendarRange,
      selectedDateISO,
      calendarPricesEnabled,
      startISO,
    ],
  );

  const [visibleRange, setVisibleRange] = useState(null);
  const visibleMarkupShownRef = useRef(false);
  const handleVisibleRangeChange = useCallback((start, count) => {
    setVisibleRange({ start, count });
  }, []);

  const returnDateISO =
    way === 'departure' && isRoundTrip && returnDateMoment.isValid()
      ? returnDateMoment.format('YYYY-MM-DD')
      : null;

  const pricedDays = buildDays(startISO, endISO).map((day) => {
    const dateCacheKey = cacheKey(day.dateISO);
    const basePrice = calendarPricesEnabled
      ? calendarPrices[dateCacheKey] ?? calendarPriceCache[dateCacheKey] ?? 0
      : 0;
    const finalPrice = calendarPricesEnabled
      ? calendarFinalPrices[dateCacheKey] ?? calendarFinalPriceCache[dateCacheKey] ?? basePrice
      : 0;
    return { day, dateCacheKey, basePrice, finalPrice };
  });
  // BOOK-176: the days paint the arm the cards paint, read with the same rule and tab latch, and
  // gated on a day on screen differing between arms, so the carousel logs an exposure only then.
  // Once it has passed, the tab holds its arm, so the gate widens to every day: a day with markup
  // sliding out of view keeps its price instead of repainting the base one mid-transition.
  const hasMarkupDay = ({ basePrice, finalPrice }) => finalPrice > basePrice;
  const hasVisibleMarkup = Boolean(
    visibleRange &&
      pricedDays
        .slice(visibleRange.start, visibleRange.start + visibleRange.count)
        .some(hasMarkupDay),
  );
  if (hasVisibleMarkup) visibleMarkupShownRef.current = true;
  const showFinalPrice = useFinalPriceExperiment({
    hasMarkup: hasVisibleMarkup || (visibleMarkupShownRef.current && pricedDays.some(hasMarkupDay)),
    isExchange: Boolean(exchangeOperationNumbers?.length && exchangeNit),
  });

  const days = pricedDays.map(({ day, dateCacheKey, basePrice, finalPrice }) => {
    const price = showFinalPrice ? finalPrice : basePrice;
    const isLoadingPrice = Boolean(
      calendarPricesEnabled && loadingCalendarPrices[dateCacheKey] && !price,
    );
    const badge = day.dateISO === returnDateISO ? 'return' : day.badge;
    return {
      ...day,
      ...(price ? { price } : {}),
      ...(isLoadingPrice ? { isLoadingPrice } : {}),
      ...(badge ? { badge } : {}),
    };
  });

  // Subscribes this mounted carousel to module-level calendar price and loading cache updates.
  useEffect(() => {
    /**
     * Syncs this mounted carousel with the shared calendar cache.
     */
    const syncCalendarState = () => {
      setCalendarPrices({ ...calendarPriceCache });
      setCalendarFinalPrices({ ...calendarFinalPriceCache });
      setLoadingCalendarPrices({ ...calendarLoadingCache });
    };

    calendarStateListeners.add(syncCalendarState);
    syncCalendarState();

    return () => {
      calendarStateListeners.delete(syncCalendarState);
    };
  }, []);

  // Aborts pending calendar requests from previous routes when origin or destination changes.
  useEffect(() => {
    if (!originSlug || !destinationSlug) return;
    abortCalendarRequestsForOtherRoutes(calendarRouteKey);
  }, [calendarRouteKey, destinationSlug, originSlug]);

  // Aborts pending calendar price requests when price tracking/display is disabled.
  useEffect(() => {
    if (calendarPricesEnabled) return;
    abortCalendarRequestsForRoute(calendarRouteKey);
  }, [calendarPricesEnabled, calendarRouteKey]);

  // Requests the selected date window and the previous window when the carousel first loads.
  useEffect(() => {
    if (!showDaysCarousel || !days.length) return;
    requestCalendarWindow('current');
    const previousDateISO = selectedDateISO
      ? moment(selectedDateISO, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD')
      : null;
    requestCalendarWindow('previous', previousDateISO);
  }, [days.length, requestCalendarWindow, selectedDateISO, showDaysCarousel]);

  // Tracks the initial carousel impression once the visible days are available.
  useEffect(() => {
    if (!showDaysCarousel || !days.length) return;
    trackEvent(DAYS_CAROUSEL_VIEWED, {
      'Way': way,
      'Origin': originSlug,
      'Destination': destinationSlug,
      'Days Count': days.length,
    });
  }, [way, originSlug, destinationSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!showDaysCarousel) return null;

  /**
   * Navigate to a new search URL with the selected date.
   * @param {object} day - Selected day object with dateISO property.
   */
  const handleSelect = (day) => {
    if (day.dateISO === selectedDateISO) return;

    const datePosition = days.findIndex((d) => d.dateISO === day.dateISO) + 1;
    const originalSearchDate = storedOriginalSearchDate ?? selectedDateISO;
    const carouselDateDelta = moment(day.dateISO, 'YYYY-MM-DD').diff(
      moment(originalSearchDate, 'YYYY-MM-DD'),
      'days',
    );

    trackEvent(DAYS_CAROUSEL_DAY_SELECTED, {
      'Way': way,
      'Origin': originSlug,
      'Destination': destinationSlug,
      'Date': day.dateISO,
      'Price': day.price || undefined,
      'Has Price': Boolean(day.price),
      'Price Loading': Boolean(day.isLoadingPrice),
      'Trip Type': isRoundTrip ? 'round' : 'one-way',
      'Departures Count': day.departures || undefined,
      'Previous Date': selectedDateISO || undefined,
      'Date Position': datePosition,
      'Carousel Date Delta': carouselDateDelta,
    });

    const pendingScrollCount = scrollCountRef.current;
    scrollCountRef.current = 0;

    dispatch(
      setCarouselSession(way, {
        originalSearchDate,
        dateSelected: day.dateISO,
        dateDelta: carouselDateDelta,
        pendingScrollCount,
      }),
    );

    const newDate = day.dateISO;
    const newReturnDate =
      way === 'departure' &&
      isRoundTrip &&
      moment(newDate, 'YYYY-MM-DD').isAfter(moment(returnDate, 'YYYY-MM-DD'), 'day')
        ? newDate
        : returnDate;

    let url;
    if (isProviderList) {
      url = buildProviderListUrl({
        originSlug,
        destinationSlug,
        departureDate: way === 'return' ? departureDate : newDate,
        returnDate: way === 'return' ? newDate : newReturnDate,
        passengers,
        departureId,
        way,
      });
    } else if (way === 'return') {
      url = buildReturnUrl(
        originSlug,
        destinationSlug,
        departureDate,
        newDate,
        passengers,
        departureId,
      );
    } else {
      url = buildSearchUrl(
        originSlug,
        destinationSlug,
        newDate,
        isRoundTrip ? newReturnDate : null,
        passengers,
      );
    }

    if (providerId) url += `/${providerId}`;
    if (isOpen) url += '/open';
    if (location.search) url += location.search;

    history.push(url);
  };

  /**
   * Track carousel scroll direction.
   * @param {'previous'|'next'} direction - Scroll direction.
   * @param {string} visibleDateISO - Date that became visible after scrolling.
   */
  const handleScroll = (direction, visibleDateISO) => {
    scrollCountRef.current += 1;
    requestCalendarWindow(direction, visibleDateISO);
    trackEvent(DAYS_CAROUSEL_SCROLLED, {
      'Way': way,
      'Origin': originSlug,
      'Destination': destinationSlug,
      'Direction': direction,
      'Scroll Count': scrollCountRef.current,
    });
  };

  const departureMoment = getMomentFromSearchDateParam(departureDate);
  const showTodayButton = way === 'departure' || departureMoment.isSameOrBefore(todayMoment, 'day');

  return (
    <DaysCarousel
      days={days}
      selectedDate={selectedDateISO}
      onSelect={handleSelect}
      onScroll={handleScroll}
      onVisibleRangeChange={handleVisibleRangeChange}
      showTodayButton={showTodayButton}
      containerClassName={containerClassName}
    />
  );
};

DaysCarouselContainer.propTypes = {
  way: PropTypes.oneOf(['departure', 'return']).isRequired,
  containerClassName: PropTypes.string,
  isProviderList: PropTypes.bool,
};

DaysCarouselContainer.defaultProps = {
  containerClassName: '',
  isProviderList: false,
};

export default DaysCarouselContainer;
